(() => {
  if (customElements.get('loop-color-chart')) return;

  class LoopColorChart extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.colorsKey = this.dataset.colorsKey || 'NEKAS_LOOP_400ML_COLORS';
      this.seriesLabel = this.dataset.seriesLabel || 'LOOP 400 ml';
      this.colors = Array.isArray(window[this.colorsKey]) ? window[this.colorsKey] : [];
      this.variants = this.readVariants();
      this.variantByCode = this.mapVariantsByCode(this.variants);
      this.selected = new Map();
      this.filter = 'all';

      this.grid = this.querySelector('[data-loop-grid]');
      this.search = this.querySelector('[data-loop-search]');
      this.count = this.querySelector('[data-loop-count]');
      this.selection = this.querySelector('[data-loop-selection]');
      this.selectedList = this.querySelector('[data-loop-selected-list]');
      this.clearButton = this.querySelector('[data-loop-clear]');
      this.addButton = this.querySelector('[data-loop-add]');
      this.feedback = this.querySelector('[data-loop-feedback]');
      this.filterButtons = [...this.querySelectorAll('[data-loop-filter]')];
      this.stockStatus = document.getElementById(this.dataset.stockTarget || '');
      this.stockText = this.stockStatus?.querySelector('[data-loop-stock-text]');
      this.stockIcon = this.stockStatus?.querySelector('[data-loop-stock-icon]');
      this.availableColorCount = this.colors.filter((color) => {
        const variant = this.variantByCode.get(color.code);
        return this.isVariantInStock(variant);
      }).length;

      this.render();
      this.bindEvents();
      this.updateStockStatus();
      this.updateSummary();
    }

    readVariants() {
      const source = this.querySelector('[data-loop-variants]');
      if (!source) return [];

      try {
        return JSON.parse(source.textContent);
      } catch (error) {
        return [];
      }
    }

    mapVariantsByCode(variants) {
      const mapped = new Map();

      variants.forEach((variant) => {
        const searchable = [variant.sku, variant.title, ...(variant.options || [])]
          .filter(Boolean)
          .join(' ')
          .toUpperCase();
        const match = searchable.match(/\bLP[\s-]?(\d{3})\b/);
        if (!match) return;

        const code = `LP-${match[1]}`;
        if (!mapped.has(code)) mapped.set(code, variant);
      });

      return mapped;
    }

    bindEvents() {
      this.search?.addEventListener('input', () => this.applyFilters());
      this.clearButton?.addEventListener('click', () => this.clearSelection());
      this.addButton?.addEventListener('click', () => this.addSelectedColors());

      this.filterButtons.forEach((button) => {
        button.addEventListener('click', () => {
          this.filter = button.dataset.loopFilter;
          this.filterButtons.forEach((candidate) => {
            const active = candidate === button;
            candidate.classList.toggle('is-active', active);
            candidate.setAttribute('aria-pressed', String(active));
          });
          this.applyFilters();
        });
      });
    }

    render() {
      if (!this.grid) return;
      const fragment = document.createDocumentFragment();

      this.colors.forEach((color) => {
        const variant = this.variantByCode.get(color.code);
        const button = document.createElement('button');
        const available = this.isVariantSelectable(variant);
        const contrast = this.getContrast(color.hex);

        button.type = 'button';
        button.className = 'loop-chart__swatch';
        button.dataset.code = color.code;
        button.dataset.name = color.name;
        button.dataset.available = String(available);
        button.style.setProperty('--loop-color', color.hex);
        button.style.setProperty('--loop-contrast', contrast);
        button.style.setProperty(
          '--loop-shadow',
          contrast === '#fff' ? 'rgba(0, 0, 0, 0.52)' : 'rgba(255, 255, 255, 0.34)'
        );
        button.setAttribute('role', 'option');
        button.setAttribute('aria-selected', 'false');
        button.setAttribute('aria-disabled', String(!available));

        const availability = variant ? (available ? 'Available' : 'Sold out') : 'Not stocked';
        button.title = `${color.name} ${color.code} - ${availability}`;

        const code = document.createElement('span');
        code.className = 'loop-chart__swatch-code';
        code.textContent = color.code;

        const name = document.createElement('span');
        name.className = 'loop-chart__swatch-name';
        name.textContent = color.name;

        button.append(code, name);
        button.addEventListener('click', () => this.toggleColor(button, color, variant));
        fragment.append(button);
      });

      this.grid.replaceChildren(fragment);
      this.applyFilters();
    }

    getContrast(hex) {
      const value = hex.replace('#', '');
      const red = parseInt(value.slice(0, 2), 16);
      const green = parseInt(value.slice(2, 4), 16);
      const blue = parseInt(value.slice(4, 6), 16);
      const luminance = (red * 299 + green * 587 + blue * 114) / 1000;
      return luminance < 142 ? '#fff' : '#111';
    }

    toggleColor(button, color, variant) {
      if (!this.isVariantSelectable(variant)) {
        this.updateStockStatus(variant || { inventoryQuantity: 0, available: false });
        this.feedback.textContent = `${color.code} is out of stock.`;
        this.feedback.classList.add('is-error');
        return;
      }

      const key = String(variant.id);
      const isSelected = this.selected.has(key);

      if (isSelected) {
        this.selected.delete(key);
      } else {
        this.selected.set(key, {
          color,
          variant,
          quantity: this.getInitialQuantity(variant),
        });
      }

      button.classList.toggle('is-selected', !isSelected);
      button.setAttribute('aria-selected', String(!isSelected));
      this.renderSelectedColors();
      this.updateStockStatus(!isSelected ? variant : this.getLastSelectedVariant());
      this.updateSummary();
    }

    getLastSelectedVariant() {
      const entries = [...this.selected.values()];
      return entries.length ? entries[entries.length - 1].variant : null;
    }

    getRuleMinimum(variant) {
      return Math.max(1, Number(variant?.quantityRule?.min) || 1);
    }

    getIncrement(variant) {
      return Math.max(1, Number(variant?.quantityRule?.increment) || 1);
    }

    getCartQuantity(variant) {
      return Math.max(0, Number(variant?.cartQuantity) || 0);
    }

    isVariantInStock(variant) {
      if (!variant?.available) return false;

      const inventoryQuantity = Math.max(0, Number(variant.inventoryQuantity) || 0);
      if (inventoryQuantity <= 0) return false;

      const ruleMinimum = this.getRuleMinimum(variant);
      const ruleMaximum = Number(variant?.quantityRule?.max);
      if (Number.isFinite(ruleMaximum) && ruleMaximum > 0 && ruleMaximum < ruleMinimum) return false;

      if (variant.inventoryManagement === 'shopify' && variant.inventoryPolicy === 'deny') {
        return inventoryQuantity >= ruleMinimum;
      }

      return true;
    }

    getRawMaximumQuantity(variant) {
      const ruleMaximum = Number(variant?.quantityRule?.max);
      const cartQuantity = this.getCartQuantity(variant);
      const limits = [];

      if (Number.isFinite(ruleMaximum) && ruleMaximum > 0) limits.push(ruleMaximum - cartQuantity);

      if (variant?.inventoryManagement === 'shopify' && variant?.inventoryPolicy === 'deny') {
        const inventoryQuantity = Math.max(0, Number(variant.inventoryQuantity) || 0);
        limits.push(inventoryQuantity - cartQuantity);
      }

      if (!limits.length) return null;
      return Math.max(0, Math.min(...limits));
    }

    getQuantityBounds(variant) {
      const ruleMinimum = this.getRuleMinimum(variant);
      const increment = this.getIncrement(variant);
      const cartQuantity = this.getCartQuantity(variant);
      const rawMaximum = this.getRawMaximumQuantity(variant);
      let minimum = ruleMinimum;

      // Match Shopify's additional-quantity rules when this variant is already in the cart.
      if (rawMaximum !== null) minimum = Math.min(minimum, rawMaximum);
      if (cartQuantity >= ruleMinimum) minimum = Math.min(minimum, increment);

      if (minimum < 1 || (rawMaximum !== null && rawMaximum < minimum)) {
        return { minimum: 0, maximum: 0, increment };
      }

      const maximum = rawMaximum === null
        ? null
        : minimum + Math.floor((rawMaximum - minimum) / increment) * increment;

      if (maximum !== null && maximum < minimum) {
        return { minimum: 0, maximum: 0, increment };
      }

      return { minimum, maximum, increment };
    }

    isVariantSelectable(variant) {
      if (!this.isVariantInStock(variant)) return false;
      const bounds = this.getQuantityBounds(variant);
      return bounds.minimum > 0 && (bounds.maximum === null || bounds.maximum >= bounds.minimum);
    }

    getInitialQuantity(variant) {
      return this.getQuantityBounds(variant).minimum;
    }

    getMaximumQuantity(variant) {
      return this.getQuantityBounds(variant).maximum;
    }

    normalizeQuantity(variant, requestedQuantity) {
      const { minimum, maximum, increment } = this.getQuantityBounds(variant);
      if (minimum < 1) return 0;
      let quantity = Number.parseInt(requestedQuantity, 10);

      if (!Number.isFinite(quantity)) quantity = minimum;
      quantity = Math.max(minimum, quantity);
      quantity = minimum + Math.round((quantity - minimum) / increment) * increment;
      if (maximum !== null) quantity = Math.min(quantity, maximum);

      return Math.max(0, quantity);
    }

    setQuantity(key, requestedQuantity, input, minusButton, plusButton) {
      const selectedColor = this.selected.get(key);
      if (!selectedColor) return;

      selectedColor.quantity = this.normalizeQuantity(selectedColor.variant, requestedQuantity);
      input.value = String(selectedColor.quantity);

      const minimum = this.getInitialQuantity(selectedColor.variant);
      const maximum = this.getMaximumQuantity(selectedColor.variant);
      const increment = Math.max(1, Number(selectedColor.variant?.quantityRule?.increment) || 1);
      minusButton.disabled = selectedColor.quantity <= minimum;
      plusButton.disabled = maximum !== null && selectedColor.quantity + increment > maximum;
      this.updateSummary();
    }

    renderSelectedColors() {
      if (!this.selectedList) return;

      const fragment = document.createDocumentFragment();

      this.selected.forEach((selectedColor, key) => {
        const row = document.createElement('div');
        row.className = 'loop-chart__selected-row';

        const identity = document.createElement('div');
        identity.className = 'loop-chart__selected-identity';

        const code = document.createElement('strong');
        code.textContent = selectedColor.color.code;

        const name = document.createElement('span');
        name.textContent = selectedColor.color.name;

        const stock = document.createElement('small');
        const inventoryQuantity = Math.max(0, Number(selectedColor.variant.inventoryQuantity) || 0);
        stock.textContent = `${inventoryQuantity} in stock`;
        identity.append(code, name, stock);

        const controls = document.createElement('div');
        controls.className = 'loop-chart__quantity';

        const minusButton = document.createElement('button');
        minusButton.type = 'button';
        minusButton.textContent = '-';
        minusButton.setAttribute('aria-label', `Decrease ${selectedColor.color.code} quantity`);

        const input = document.createElement('input');
        input.type = 'number';
        input.inputMode = 'numeric';
        input.min = String(this.getInitialQuantity(selectedColor.variant));
        input.step = String(Math.max(1, Number(selectedColor.variant?.quantityRule?.increment) || 1));
        input.value = String(selectedColor.quantity);
        input.setAttribute('aria-label', `${selectedColor.color.code} quantity`);

        const maximum = this.getMaximumQuantity(selectedColor.variant);
        if (maximum !== null) input.max = String(maximum);

        const plusButton = document.createElement('button');
        plusButton.type = 'button';
        plusButton.textContent = '+';
        plusButton.setAttribute('aria-label', `Increase ${selectedColor.color.code} quantity`);

        const removeButton = document.createElement('button');
        removeButton.type = 'button';
        removeButton.className = 'loop-chart__remove';
        removeButton.textContent = 'Remove';
        removeButton.setAttribute('aria-label', `Remove ${selectedColor.color.code}`);

        const increment = Math.max(1, Number(selectedColor.variant?.quantityRule?.increment) || 1);
        minusButton.addEventListener('click', () => {
          this.setQuantity(key, selectedColor.quantity - increment, input, minusButton, plusButton);
        });
        plusButton.addEventListener('click', () => {
          this.setQuantity(key, selectedColor.quantity + increment, input, minusButton, plusButton);
        });
        input.addEventListener('change', () => {
          this.setQuantity(key, input.value, input, minusButton, plusButton);
        });
        removeButton.addEventListener('click', () => this.removeColor(key));

        controls.append(minusButton, input, plusButton);
        row.append(identity, controls, removeButton);
        fragment.append(row);

        this.setQuantity(key, selectedColor.quantity, input, minusButton, plusButton);
      });

      this.selectedList.replaceChildren(fragment);
      this.selectedList.hidden = this.selected.size === 0;
    }

    removeColor(key) {
      const selectedColor = this.selected.get(key);
      if (!selectedColor) return;

      this.selected.delete(key);
      const swatch = [...this.querySelectorAll('.loop-chart__swatch')].find(
        (candidate) => candidate.dataset.code === selectedColor.color.code
      );
      swatch?.classList.remove('is-selected');
      swatch?.setAttribute('aria-selected', 'false');
      this.renderSelectedColors();
      this.updateStockStatus(this.getLastSelectedVariant());
      this.updateSummary();
    }

    updateStockStatus(variant = null) {
      if (!this.stockStatus || !this.stockText) return;

      let label;
      let color;

      if (variant) {
        const quantity = Math.max(0, Number(variant.inventoryQuantity) || 0);
        if (this.isVariantInStock(variant)) {
          label = `${quantity} in stock`;
          color = 'rgb(62, 214, 96)';
        } else {
          label = 'Out of stock';
          color = 'rgb(200, 200, 200)';
        }
      } else if (this.availableColorCount > 0) {
        label = `${this.availableColorCount} ${this.availableColorCount === 1 ? 'color' : 'colors'} in stock`;
        color = 'rgb(62, 214, 96)';
      } else {
        label = 'Out of stock';
        color = 'rgb(200, 200, 200)';
      }

      this.stockText.textContent = label;
      if (this.stockIcon) this.stockIcon.style.color = color;
      this.stockStatus.classList.remove('hidden');
    }

    applyFilters() {
      const query = (this.search?.value || '').trim().toUpperCase();
      let visible = 0;
      const swatches = [...this.querySelectorAll('.loop-chart__swatch')];

      swatches.forEach((swatch) => {
        const matchesSearch = !query || `${swatch.dataset.code} ${swatch.dataset.name}`.includes(query);
        const matchesAvailability = this.filter === 'all' || swatch.dataset.available === 'true';
        const show = matchesSearch && matchesAvailability;
        swatch.hidden = !show;
        if (show) visible += 1;
      });

      const available = this.availableColorCount;
      this.count.textContent = query
        ? `${visible} matching colors - ${available} available in this shop`
        : `${this.colors.length} ${this.seriesLabel} chart colors - ${available} available in this shop`;
    }

    updateSummary() {
      const totalColors = this.selected.size;
      const totalCans = [...this.selected.values()].reduce(
        (sum, selectedColor) => sum + Number(selectedColor.quantity || 0),
        0
      );
      this.selection.textContent = totalColors === 0
        ? '0 colors selected'
        : `${totalColors} ${totalColors === 1 ? 'color' : 'colors'} selected | ${totalCans} ${totalCans === 1 ? 'can' : 'cans'}`;
      this.clearButton.hidden = totalColors === 0;
      this.addButton.disabled = totalColors === 0 || totalCans === 0;
      this.addButton.textContent = totalColors === 0
        ? 'Add selected colors'
        : `Add ${totalCans} ${totalCans === 1 ? 'can' : 'cans'} to cart`;
      this.feedback.textContent = '';
      this.feedback.classList.remove('is-error');
    }

    clearSelection() {
      this.selected.clear();
      this.querySelectorAll('.loop-chart__swatch.is-selected').forEach((swatch) => {
        swatch.classList.remove('is-selected');
        swatch.setAttribute('aria-selected', 'false');
      });
      this.renderSelectedColors();
      this.updateStockStatus();
      this.updateSummary();
    }

    async addSelectedColors() {
      if (this.selected.size === 0 || this.addButton.disabled) return;

      const items = [...this.selected.values()].map(({ variant, quantity }) => ({
        id: Number(variant.id),
        quantity: Number(quantity),
      }));
      const totalCans = items.reduce((sum, item) => sum + item.quantity, 0);
      const originalLabel = this.addButton.textContent;
      this.addButton.disabled = true;
      this.addButton.textContent = 'Adding...';
      this.feedback.textContent = '';
      this.feedback.classList.remove('is-error');

      try {
        const response = await fetch(this.dataset.cartUrl, {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            items,
            sections: ['cart-icon-bubble'],
            sections_url: window.location.pathname,
          }),
        });
        const result = await response.json();

        if (!response.ok) {
          throw new Error(result.description || result.message || 'The selected colors could not be added.');
        }

        items.forEach((item) => {
          const selectedColor = this.selected.get(String(item.id));
          if (!selectedColor) return;
          selectedColor.variant.cartQuantity = this.getCartQuantity(selectedColor.variant) + item.quantity;
        });

        window.NEKASCartUI?.updateCartIcon(result.sections?.['cart-icon-bubble']);
        this.clearSelection();
        this.feedback.textContent = `${totalCans} ${totalCans === 1 ? 'can' : 'cans'} across ${items.length} ${items.length === 1 ? 'color' : 'colors'} added to your cart.`;
      } catch (error) {
        this.feedback.textContent = error.message || 'The selected colors could not be added.';
        this.feedback.classList.add('is-error');
        this.addButton.disabled = false;
        this.addButton.textContent = originalLabel;
      }
    }
  }

  customElements.define('loop-color-chart', LoopColorChart);
})();
